/**
 * Date Formatting Utilities
 * 
 * Provides functions for formatting ISO date strings to local datetime strings
 * with support for Ukraine timezone (Europe/Kiev) and customizable formatting options.
 * All functions handle null/undefined inputs gracefully by returning '-'.
 */

/**
 * Formats an ISO date string to local datetime string
 * @param isoDateString - ISO date string (e.g., "2025-10-16T20:30:23.736Z") or null/undefined
 * @param options - Intl.DateTimeFormatOptions for customization (optional)
 * @returns Formatted local datetime string in uk-UA locale, or '-' if input is invalid
 * @example
 * formatToLocalDateTime("2025-10-16T20:30:23.736Z")
 * // Returns: "16.10.2025, 23:30:23"
 */
export const formatToLocalDateTime = (
  isoDateString: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string => {
  if (!isoDateString) return '-';
  
  try {
    const date = new Date(isoDateString);
    
    // Check if date is valid
    if (isNaN(date.getTime())) return '-';
    
    const defaultOptions: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Kiev'
    };
    
    const formatOptions = { ...defaultOptions, ...options };
    
    return date.toLocaleString('uk-UA', formatOptions);
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

/**
 * Formats an ISO date string to local date only (without time)
 * @param isoDateString - ISO date string or null/undefined
 * @returns Formatted local date string in uk-UA locale, or '-' if input is invalid
 * @example
 * formatToLocalDate("2025-10-16T20:30:23.736Z")
 * // Returns: "16.10.2025"
 */
export const formatToLocalDate = (isoDateString: string | null | undefined): string => {
  return formatToLocalDateTime(isoDateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });
};

/**
 * Formats an ISO date string to local datetime without seconds
 * @param isoDateString - ISO date string or null/undefined
 * @returns Formatted local datetime string without seconds in uk-UA locale, or '-' if input is invalid
 * @example
 * formatToLocalDateTimeShort("2025-10-16T20:30:23.736Z")
 * // Returns: "16.10.2025, 23:30"
 */
export const formatToLocalDateTimeShort = (isoDateString: string | null | undefined): string => {
  return formatToLocalDateTime(isoDateString, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};
